import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Controller, useForm } from 'react-hook-form';
import { useEffect, useState as classicUseState } from 'react';
import { useState } from '@hookstate/core';
import { useMutation, useQueryClient } from 'react-query';
import { CategoryColor, ITagCategory } from '@models/TagCategory.model';
import { updateTagCategory } from '@services/queries/tagsCategory/updateTagCategory/updateTagCategory';
import { Toggle } from '@components/Toggle/Toggle';

import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Confirm,
  Line,
  Form,
  Divider,
  Label,
  Input,
  Textarea,
  Row,
  Color,
  ColorButton,
  Error,
  ToggleWithName,
  ToggleName,
} from './EditCategorieDialog.styles';

interface IEditCategorieDialog {
  open: boolean;
  onClose: () => void;
  tagCategory: ITagCategory;
}

interface IFormData {
  name: string;
  description: string;
  color: CategoryColor;
  showOnOnboard: boolean;
}

export const EditCategorieDialog = ({
  open,
  onClose,
  tagCategory,
}: IEditCategorieDialog) => {
  const portalDiv = document.getElementById('portal');

  const [color, setColor] = classicUseState<CategoryColor>(
    tagCategory.color || CategoryColor.UNCATEGORIZED,
  );

  const isEdited = useState<boolean>(false);

  const queryClient = useQueryClient();

  const schema = yup
    .object({
      name: yup.string().required(),
      description: yup.string(),
      color: yup.mixed<CategoryColor>().oneOf(Object.values(CategoryColor)),
      showOnOnboard: yup.string().required(),
    })
    .required();

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    control,
    setError,
    setValue,
    reset,
    clearErrors,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: tagCategory.name,
      description: tagCategory.description || '',
      color: tagCategory.color || CategoryColor.UNCATEGORIZED,
      showOnOnboard: tagCategory.showOnOnboard || false,
    },
    mode: 'onChange',
  });

  const close = () => {
    onClose();
    reset();
    isEdited.set(false);
    clearErrors('name');
  };

  useEffect(() => {
    setValue('name', tagCategory.name);
    setValue('description', tagCategory.description || '');
    setValue('showOnOnboard', tagCategory.showOnOnboard || false);
  }, [tagCategory]);

  const updateTagCategoryMutation = useMutation(updateTagCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tagCategories']);
      close();
    },
    onError: (error: any) => {
      if (
        error.response?.data?.message ===
        'There is already a tag category with this name'
      ) {
        setError('name', {
          type: 'validate',
          message: 'There is already a tag category with this name',
        });
      }
    },
  });

  const onSubmit = (data: IFormData) => {
    updateTagCategoryMutation.mutate({
      id: tagCategory.id,
      name: data.name,
      description: data.description,
      color,
      showOnOnboard: data.showOnOnboard,
    });
  };

  const CategoryNotSelected = () => {
    if (!color) return true;
    return false;
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Edit Category</Title>
              <Close onClick={close}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider height="28px" />
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Label className="mandatory">Category name</Label>

              <Input
                placeholder="Ex.: Location"
                {...register('name')}
                onChange={() => isEdited.set(true)}
                isError={
                  errors.name &&
                  errors.name.message ===
                    'There is already a tag category with this name'
                }
              />
              {errors.name &&
                errors.name.message ===
                  'There is already a tag category with this name' && (
                  <Error>{errors.name.message}</Error>
                )}

              <Divider height="16px" />

              <Label className="mandatory">Description</Label>

              <Textarea
                placeholder=""
                {...register('description')}
                onChange={() => isEdited.set(true)}
              />

              <Divider height="16px" />

              <Label className="mandatory">Show on Onboard</Label>

              <Controller
                control={control}
                name="showOnOnboard"
                render={({ field: { onChange, value } }) => (
                  <ToggleWithName>
                    <Toggle
                      selected={value}
                      toggleSelected={() => {
                        onChange(!value);
                        isEdited.set(true);
                      }}
                    />
                    <ToggleName>{value ? 'Yes' : 'No'}</ToggleName>
                  </ToggleWithName>
                )}
              />

              <Divider height="24px" />

              <Label className="mandatory">Category Color</Label>
              <Label className="description">
                This will define the display color for all tags of this category
              </Label>

              <Divider height="16px" />

              <Row>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.BLUE);
                    isEdited.set(true);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.BLUE}
                    color={CategoryColor.BLUE}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.GREEN);
                    isEdited.set(true);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.GREEN}
                    color={CategoryColor.GREEN}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.ORANGE);
                    isEdited.set(true);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.ORANGE}
                    color={CategoryColor.ORANGE}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.PINK);
                    isEdited.set(true);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.PINK}
                    color={CategoryColor.PINK}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.RED);
                    isEdited.set(true);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.RED}
                    color={CategoryColor.RED}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.TEAL);
                    isEdited.set(true);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.TEAL}
                    color={CategoryColor.TEAL}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.VIOLET);
                    isEdited.set(true);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.VIOLET}
                    color={CategoryColor.VIOLET}
                  />
                </ColorButton>
                <ColorButton
                  type="button"
                  onClick={() => {
                    setColor(CategoryColor.YELLOW);
                    isEdited.set(true);
                  }}
                >
                  <Color
                    selected={color === CategoryColor.YELLOW}
                    color={CategoryColor.YELLOW}
                  />
                </ColorButton>
              </Row>

              <Line>
                <Confirm
                  type="submit"
                  disabled={
                    !isValid || CategoryNotSelected() || !isEdited.value
                  }
                >
                  Save
                </Confirm>
              </Line>
            </Form>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
